import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormCheckbox } from '@/components/forms/form-checkbox';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';
import { useAllMinimalOrgs } from '@/lib/api-call-hooks';
import { createSchool, getSchools } from '@/lib/api-calls';
import { disallowedEmailDomains, internationalPhoneRegex } from '@/lib/string-utils';
import { optionalEmail } from '@airreading/common';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { CreateOrg } from '../../-components/create-org';
import { useState } from 'react';
import { Org } from '@airreading/common/data';

const formSchema = z.object({
  name: z.string(),
  orgId: z.string(),
  totalSeats: z.coerce
    .number({ message: 'Please enter a valid number.' })
    .int({ message: 'Please enter a integer number.' })
    .min(0)
    .default(0)
    .optional(),
  email: optionalEmail.refine(
    (val) => !val || !disallowedEmailDomains.some((domain) => val.toLowerCase().endsWith(domain)),
    {
      message: 'Email must be personal, not corporate',
    },
  ),
  phone: z
    .string()
    .optional()
    .refine((val) => !val || internationalPhoneRegex.test(val), {
      message: 'Phone number must be in international format (e.g. +1, +54)',
    }),
  address: z.string().optional(),
  schoolAdminName: z.string().optional(),
  needSpanishFeedback: z.boolean().optional().default(false),
});

export const CreateSchool = () => {
  const { mutateAsync } = useMutation({
    mutationFn: createSchool,
  });

  const { data: allOrgs = [], isPending: isLoadingOrgs } = useAllMinimalOrgs();
  const [createdOrg, setCreatedOrg] = useState<Org | null>(null);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const onSubmit = async ({ formData, closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    try {
      await mutateAsync({
        ...formData,
        edlinkId: undefined,
        orgId: formData.orgId,
      });
      toast({
        title: 'School created successfully',
      });
      closeModal();
      queryClient.invalidateQueries(getSchools());
    } catch {
      toast({
        title: 'School creation failed',
      });
    }
  };

  return (
    <FormModal
      modalTitle="Add School"
      onSubmit={onSubmit}
      formSchema={formSchema}
      trigger={
        <Button loading={isLoadingOrgs} onClick={() => setCreatedOrg(null)}>
          Add School
        </Button>
      }
      confirmBtnLabel="Add"
    >
      {({ form }) => {
        if (createdOrg && !form.getValues('orgId')) {
          form.setValue('orgId', createdOrg.id);
        }

        return (
          <>
            <div className="grid gap-4 py-4 grid-cols-2">
              <FormInputField
                name="name"
                label="School name"
                form={form}
                inputProps={{ className: 'w-full', required: true }}
              />
              <FormInputField
                name="totalSeats"
                label="Total seats"
                form={form}
                inputProps={{
                  type: 'number',
                  className: 'w-full',
                }}
              />
              <div className="col-span-2 grid grid-cols-6 items-center gap-4">
                <FormSingleSelect
                  label="District"
                  options={allOrgs.map((org) => org.id)}
                  form={form}
                  name="orgId"
                  renderOption={(id) => allOrgs.find((org) => org.id === id)?.name || id}
                  className="col-span-5"
                  selectProps={{
                    required: true,
                    value: form.watch('orgId') ?? undefined,
                  }}
                />
                <CreateOrg
                  onCreated={(org) => {
                    setCreatedOrg(org);
                    form.setValue('orgId', org.id);
                  }}
                  trigger={<Button>Add District</Button>}
                />
              </div>
              <FormInputField
                name="schoolAdminName"
                label="School admin name"
                form={form}
                className="col-span-2"
                inputProps={{ className: 'w-full' }}
              />
              <FormInputField name="email" label="Email" form={form} inputProps={{ className: 'w-full' }} />
              <FormInputField name="phone" label="Phone" form={form} inputProps={{ className: 'w-full' }} />
              <FormInputField
                name="address"
                label="Address"
                form={form}
                className="col-span-2"
                inputProps={{ className: 'w-full' }}
              />
              <FormCheckbox name="needSpanishFeedback" label="Need Spanish feedback" form={form} />
            </div>
          </>
        );
      }}
    </FormModal>
  );
};
